<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuth } from '@/composables/useAuth';
import { useNotifications } from '@/composables/useNotifications';
import { ArrowDown, Collection, Lock, Setting, SwitchButton } from '@element-plus/icons-vue';
import zhCn from 'element-plus/es/locale/lang/zh-cn';
import AppSidebar from '@/components/layout/AppSidebar.vue';
import UserAvatar from '@/components/common/UserAvatar.vue';
import LogoutConfirm from '@/components/auth/LogoutConfirm.vue';
import NotificationBadge from '@/components/notifications/NotificationBadge.vue';
import CreateOrgPrompt from '@/components/personal-space/CreateOrgPrompt.vue';
import PersonalSpaceBanner from '@/components/personal-space/PersonalSpaceBanner.vue';
import ApplicationStatusBadge from '@/components/personal-space/ApplicationStatusBadge.vue';
import SystemAlertBanner from '@/components/common/SystemAlertBanner.vue';
import { hasAdminRole } from '@/types/auth';
import { passwordPagePath } from '@/utils/passwordEntry';
import { userRoleMap, userRoleTagType } from '@/types/user';
import type { UserRole } from '@/types/auth';
import { useSystemErrorStore } from '@/stores/systemError'
import { useBrandingStore } from '@/stores/branding'

const router = useRouter();
const route = useRoute();
const { state, logout } = useAuth();
const sysErr = useSystemErrorStore();
const brandingStore = useBrandingStore();
const showLogoutConfirm = ref(false);

// 系统名称/Logo 来自管理端"基础设置"（公开接口，失败回退默认品牌名）
const systemName = computed(() => brandingStore.systemName || '智台账');
const brandLogo = computed(() => brandingStore.logo);

onMounted(() => {
  brandingStore.loadBranding();
});

// 启动通知中心全局监听（页面可见性自动刷新）
useNotifications();

const isAuthenticated = computed(() => state.isAuthenticated);
const userName = computed(() => state.currentUser?.displayName ?? '');
const userAvatarUrl = computed(() => state.currentUser?.avatarUrl || '');
const isAdmin = computed(() => hasAdminRole(state.currentUser?.role));
const isPersonalSpace = computed(() => state.currentUser?.organizationType === 'personal');

// 改密入口按角色分流（分流口径单源在 utils/passwordEntry，与登录页强制改密引导共用）
const passwordPageTarget = computed(() => passwordPagePath(state.currentUser?.role));

// 监听登出：认证状态变为 false 且当前不在公开页时，立即跳转到登录页，
// 避免 /admin 等受保护页面组件在未登录状态下重新挂载并发起 401 请求。
watch(isAuthenticated, (authenticated) => {
  if (!authenticated && route.meta.requiresAuth) {
    router.replace('/login');
  }
});

// 使用 userRoleMap 精确映射角色标签
const userRoleLabel = computed(() => {
  if (isPersonalSpace.value) return '个人体验空间';
  const role = state.currentUser?.role as UserRole | undefined;
  if (role && userRoleMap[role]) return userRoleMap[role];
  return '普通用户';
});

const roleTagType = computed(() => {
  if (isPersonalSpace.value) return 'warning' as const;
  const role = state.currentUser?.role as UserRole | undefined;
  if (role && userRoleTagType[role]) return userRoleTagType[role];
  return 'info' as const;
});

// Banner dismiss logic (7 days, stored as date string to avoid client clock manipulation)
const BANNER_DISMISS_KEY = 'personal_space_banner_dismissed_until';
const readBannerDismissed = () => {
  const dismissedUntil = localStorage.getItem(BANNER_DISMISS_KEY);
  return !!dismissedUntil && new Date().toISOString().slice(0, 10) <= dismissedUntil;
};
// 必须是 ref 而非 computed：该值只来自 localStorage（无响应式依赖），computed 首次求值后
// 永久缓存，而 banner-dismissed 监听体为空，导致点 × 后横幅在刷新前不会消失
const isBannerDismissed = ref(readBannerDismissed());
const handleBannerDismissed = () => {
  isBannerDismissed.value = readBannerDismissed();
};

// CreateOrgPrompt state — dismiss check lifted to parent to avoid mount-then-hide flicker
const showCreateOrgPrompt = ref(false);
const promptTrigger = ref<'project_limit' | 'ai_quota' | 'member_invite' | 'active_conversion'>('project_limit');

function shouldShowPrompt(trigger: string): boolean {
  const key = `create_org_prompt_dismissed_${trigger}`;
  const dismissedUntil = localStorage.getItem(key);
  if (!dismissedUntil) return true;
  return new Date().toISOString().slice(0, 10) <= dismissedUntil;
}

function handlePersonalSpaceLimit(e: Event) {
  const detail = (e as CustomEvent).detail;
  const codeMap: Record<number, typeof promptTrigger.value> = {
    4401: 'project_limit',
    4402: 'ai_quota',
    4403: 'member_invite',
    4404: 'active_conversion',
  };
  const trigger = codeMap[detail.code] || 'active_conversion';
  if (!shouldShowPrompt(trigger)) return;
  promptTrigger.value = trigger;
  showCreateOrgPrompt.value = true;
}

onMounted(() => {
  // 修复工作记录审查#10：删 migrateWorkRecordData——旧 localStorage 工作记录数据迁移，系统未上线无旧数据
  window.addEventListener('personal-space-limit', handlePersonalSpaceLimit);
  window.addEventListener('banner-dismissed', handleBannerDismissed);
  setupGlobalErrorListeners();
});

onUnmounted(() => {
  window.removeEventListener('personal-space-limit', handlePersonalSpaceLimit);
  window.removeEventListener('banner-dismissed', handleBannerDismissed);
  teardownGlobalErrorListeners();
});

// ==================== 全局系统错误监听 ====================

let successRequestCount = 0

function handleSystemError(e: Event): void {
  const detail = (e as CustomEvent).detail
  if (!detail || !detail.category) return

  // 仅拦截需要全局展示的异常（网络/超时/5xx）
  if (!detail.shouldShowGlobalAlert) return

  // 401 不显示横幅（走自动登出流程）
  if (detail.category === 'unauthorized') return

  sysErr.setError({
    category: detail.category,
    message: detail.userMessage,
    status: detail.status,
    errorCode: detail.errorCode,
  })
}

function onSuccessfulRequest(): void {
  successRequestCount++
  if (successRequestCount >= 3) {
    sysErr.clear()
    successRequestCount = 0
  }
}

function setupGlobalErrorListeners(): void {
  window.addEventListener('system-error', handleSystemError)
  window.addEventListener('http-success', () => {
    onSuccessfulRequest()
  })
}

function teardownGlobalErrorListeners(): void {
  window.removeEventListener('system-error', handleSystemError)
  window.removeEventListener('http-success', onSuccessfulRequest)
}

// 判断当前是否在 dashboard/admin 等内部页面（供模板使用）
// /setup 为全屏快速设置页，不显示顶部栏和侧边栏
const isAppPage = computed(() => {
  const appPaths = ['/dashboard', '/admin', '/ledger', '/ledgers', '/records', '/tasks', '/reports', '/analysis', '/messages', '/settings', '/inspections', '/document', '/workspace'];
  return appPaths.some(p => route.path === p || route.path.startsWith(p + '/'));
});

// 路由过渡 key：取顶层路由路径，避免嵌套路由切换时不必要地销毁重建父组件
const layoutKey = computed(() => route.matched[0]?.path ?? route.path);

function goHome() {
  if (isAdmin.value) {
    router.push('/admin');
  } else {
    router.push('/dashboard');
  }
}

async function handleLogoutConfirm() {
  await logout();
  // 登出后跳转登录页（watch 兜底已 replace 到 /login，此处重复导航会被路由忽略）
  router.push('/login');
}
</script>

<template>
  <el-config-provider :locale="zhCn">
    <div class="min-h-screen">
    <!-- 全局顶部栏（登录后显示） -->
    <header
      v-if="isAuthenticated && isAppPage"
      class="app-header flex items-center justify-between border-b border-[var(--color-divider)] px-5 py-2.5 md:px-6"
    >
      <div class="flex items-center gap-3">
        <!-- Logo / 首页入口 -->
        <button
          class="logo-btn flex items-center gap-2.5 text-lg font-bold text-[var(--color-primary)] transition-all duration-300 hover:opacity-80"
          @click="goHome"
        >
          <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] shadow-sm overflow-hidden">
            <img v-if="brandLogo" :src="brandLogo" :alt="systemName" class="h-full w-full object-cover" />
            <ElIcon v-else size="16" color="#fff"><Collection /></ElIcon>
          </div>
          <span class="hidden sm:inline tracking-tight">{{ systemName }}</span>
        </button>
        <!-- 面包屑分隔 -->
        <span class="hidden text-[var(--color-divider)] sm:inline">|</span>
        <span class="hidden text-sm font-medium text-[var(--color-caption)] sm:inline">
          {{ isAdmin ? '管理后台' : '工作台' }}
        </span>
      </div>

      <div class="flex items-center gap-4">
        <!-- 申请状态徽章 -->
        <ApplicationStatusBadge />
        <!-- 通知角标 -->
        <NotificationBadge />

        <!-- 角色标签 / 个人体验空间标识 -->
        <ElTag
          :type="roleTagType"
          size="small"
          effect="plain"
          class="hidden md:block"
          round
        >
          {{ userRoleLabel }}
        </ElTag>

        <!-- 用户菜单 -->
        <ElDropdown trigger="click" :teleported="true" popper-class="user-dropdown-popper">
          <div class="user-menu-trigger flex cursor-pointer items-center gap-2.5 rounded-xl px-3 py-1.5 transition-all duration-200 hover:bg-[var(--color-primary-light)]">
            <div class="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-secondary)] text-sm font-semibold text-white shadow-sm">
              <UserAvatar :src="userAvatarUrl">
                {{ userName.charAt(0) }}
              </UserAvatar>
            </div>
            <span class="hidden max-w-[100px] truncate text-sm font-medium text-[var(--color-title)] sm:block">{{ userName }}</span>
            <ElIcon class="text-[var(--color-caption)] transition-transform duration-200"><ArrowDown /></ElIcon>
          </div>
          <template #dropdown>
            <ElDropdownMenu>
              <ElDropdownItem v-if="isPersonalSpace" @click="router.push('/setup')">
                <ElIcon class="mr-2"><Collection /></ElIcon>
                完善资料
              </ElDropdownItem>
              <ElDropdownItem @click="router.push(passwordPageTarget)">
                <ElIcon class="mr-2"><Lock /></ElIcon>
                修改密码
              </ElDropdownItem>
              <ElDropdownItem @click="router.push('/settings')">
                <ElIcon class="mr-2"><Setting /></ElIcon>
                账号设置
              </ElDropdownItem>
              <ElDropdownItem divided @click="showLogoutConfirm = true">
                <ElIcon class="mr-2"><SwitchButton /></ElIcon>
                退出登录
              </ElDropdownItem>
            </ElDropdownMenu>
          </template>
        </ElDropdown>
      </div>
    </header>

    <!-- 内部页面布局：侧边栏 + 主内容 -->
    <div
      v-if="isAuthenticated && isAppPage"
      class="flex h-[calc(100vh-53px)] bg-[var(--color-page)] overflow-hidden"
    >
      <AppSidebar />
      <!-- 横幅与页面内容分开占位：此前 main 既是滚动容器又与横幅并列，页面根节点写
           height: 100% 时会与横幅（个人体验空间横幅高约 41px）叠加溢出，底部固定区域
           （如知识库问答的输入框）被挤出可视区。改为横幅固定、页面在剩余空间内自成滚动容器。 -->
      <main class="flex min-w-0 flex-1 flex-col overflow-hidden">
        <PersonalSpaceBanner
          v-if="isPersonalSpace && !isBannerDismissed"
          class="flex-shrink-0"
        />
        <div class="min-h-0 flex-1 overflow-auto">
          <router-view />
        </div>
      </main>
    </div>
    <!-- 公开页面 / 非应用页面 -->
    <div v-else>
      <router-view v-slot="{ Component }">
        <Transition name="page" mode="out-in">
          <component :is="Component" :key="layoutKey" />
        </Transition>
      </router-view>
    </div>

    <!-- 全局系统异常横幅 -->
    <SystemAlertBanner />

    <LogoutConfirm
      v-model:visible="showLogoutConfirm"
      @confirm="handleLogoutConfirm"
    />

    <!-- Personal Space Limit Prompt -->
    <CreateOrgPrompt
      v-if="showCreateOrgPrompt"
      :trigger="promptTrigger"
      @dismiss="showCreateOrgPrompt = false"
    />
  </div>
</el-config-provider></template>

<style scoped>
/* 修复下拉菜单展开时整体页面晃动：固定 header 高度并始终显示 body 滚动条 */
:global(html) {
  overflow-y: scroll;
}
:global(body) {
  overflow-y: scroll;
}

/* 修复弹窗(对话框/消息框/抽屉)开关时整体抖动：
 * EP 打开弹窗时给 body 加 el-popup-parent--hidden(overflow:hidden)，
 * 会收走 body 因上面 overflow-y:scroll 而预留的 6px 滚动条槽，内容区随之
 * 增减 6px；且 EP 实测滚动条宽度在某些环境为 0，宽度补偿不触发。
 * 此处锁定期间保持滚动条槽不动，并抵消 EP 可能写入的内联宽度补偿，布局零变化。 */
:global(body.el-popup-parent--hidden) {
  overflow-y: scroll !important;
  width: auto !important;
}

.app-header {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  z-index: 100;
  position: sticky;
  top: 0;
  height: 53px;
  box-sizing: border-box;
  flex-shrink: 0;
}

html.dark .app-header {
  background: rgba(26, 29, 35, 0.85);
}

.logo-btn:hover {
  transform: scale(1.02);
}

.user-menu-trigger:hover :deep(.el-icon) {
  transform: rotate(180deg);
}

/* 用户下拉菜单使用独立定位层级，避免撑开 header 影响主布局 */
:global(.user-dropdown-popper) {
  z-index: 2000 !important;
}
</style>
